import type { NavItemConfigInternal, Store, UserMenuData, UserMenuDataShapes } from '../../types';
import { getMenuIdsByVisible } from '../get-menu-ids-by-visible';
import { repairGlobalMenuItems } from '../repair-global-menu-items';
import { repairItems } from '../repair-items';

type BuildUserMenuDataShapesParams = {
	allItemMenuIds: string[];
	allShortcutMenuIds: string[];
	defaultMenuVisibility?: UserMenuData;
	forYouMenuId: string;
	globalItems: Map<string, NavItemConfigInternal>;
	rawUserMenuData: UserMenuData | null | undefined;
	setUserMenuData: Store['setUserMenuData'];
};

// Build common shapes out of user menu data.
// We do it here, once, so components don't perform the same filtering and mapping in more than one place.
export function buildUserMenuDataShapes(params: BuildUserMenuDataShapesParams): UserMenuDataShapes {
	const {
		allItemMenuIds,
		allShortcutMenuIds,
		forYouMenuId,
		globalItems,
		rawUserMenuData,
		setUserMenuData,
		defaultMenuVisibility,
	} = params;

	// Check whether customisation data is empty
	const userDataIsEmpty = !hasUserData(rawUserMenuData);

	// Remove any menu ids that overlap with shortcuts using Set for O(1) lookups
	const shortcutMenuIdsSet = new Set(allShortcutMenuIds);
	const distinctDefaultItemMenuIds = allItemMenuIds.filter((id) => !shortcutMenuIdsSet.has(id));

	const repairedUserMenuData = repairGlobalMenuItems({
		forYouMenuId,
		globalItems,
		rawUserMenuData,
	});

	// Add missing items and remove obsolete or duplicate items
	const itemUserMenuData = userDataIsEmpty
		? repairWithDefaults(rawUserMenuData, defaultMenuVisibility, distinctDefaultItemMenuIds)
		: repair(repairedUserMenuData, distinctDefaultItemMenuIds);

	// Add missing shortcuts and remove obsolete or duplicate shortcuts
	const shortcutUserMenuData = userDataIsEmpty
		? repairWithDefaults(rawUserMenuData, defaultMenuVisibility, allShortcutMenuIds)
		: repair(repairedUserMenuData, allShortcutMenuIds);

	// This repaired user menu data should always be used instead of the raw user menu data
	const userMenuData = itemUserMenuData.concat(shortcutUserMenuData);

	// Split the user menu data into visible and hidden menu ids, taking into account any visibility overrides from props
	const itemUserMenuDataWithMaybeDefaultsApplied = userDataIsEmpty
		? setDefaultItemVisibility(itemUserMenuData, defaultMenuVisibility)
		: itemUserMenuData;
	const shortcutUserMenuDataWithMaybeDefaultsApplied = userDataIsEmpty
		? setDefaultItemVisibility(shortcutUserMenuData, defaultMenuVisibility)
		: shortcutUserMenuData;

	const [visibleItemMenuIds, hiddenItemMenuIds] = getMenuIdsByVisible(
		itemUserMenuDataWithMaybeDefaultsApplied,
	);
	const [visibleShortcutMenuIds, hiddenShortcutMenuIds] = getMenuIdsByVisible(
		shortcutUserMenuDataWithMaybeDefaultsApplied,
	);
	const visibleMenuIds = visibleItemMenuIds.concat(visibleShortcutMenuIds);
	const hiddenMenuIds = hiddenItemMenuIds.concat(hiddenShortcutMenuIds);

	return {
		hiddenItemMenuIds,
		hiddenMenuIds,
		hiddenShortcutMenuIds,
		itemUserMenuData: itemUserMenuDataWithMaybeDefaultsApplied,
		shortcutUserMenuData: shortcutUserMenuDataWithMaybeDefaultsApplied,
		setUserMenuData,
		userMenuData,
		visibleItemMenuIds,
		visibleMenuIds,
		visibleShortcutMenuIds,
	};
}

const repair = (rawUserMenuData: UserMenuData | null | undefined, defaultMenuIds: string[]) =>
	repairItems(rawUserMenuData ?? [], createDefaultItems(defaultMenuIds));

const repairWithDefaults = (
	rawUserMenuData: UserMenuData | null | undefined,
	defaultVisibilityOverride: UserMenuData | undefined,
	defaultMenuIds: string[],
) => setDefaultItemVisibility(repair(rawUserMenuData, defaultMenuIds), defaultVisibilityOverride);

const createDefaultItems = (menuIds: string[]): UserMenuData =>
	menuIds.map((menuId) => ({ menuId, visible: true }));

const setDefaultItemVisibility = (
	userMenuData: UserMenuData,
	defaultMenuVisibility: UserMenuData | undefined,
): UserMenuData => {
	if (!defaultMenuVisibility) {
		return userMenuData;
	}

	const overrideMap = new Map(defaultMenuVisibility.map((item) => [item.menuId, item.visible]));

	return userMenuData.map((item) => {
		const overrideVisible = overrideMap.get(item.menuId);
		return overrideVisible !== undefined ? { ...item, visible: overrideVisible } : item;
	});
};

const hasUserData = (rawUserMenuData: UserMenuData | null | undefined): boolean =>
	Array.isArray(rawUserMenuData) && rawUserMenuData.length > 0;
